import Box from "@mui/material/Box";

import { useEffect, useState } from "react";
import CircularProgress from "@mui/material/CircularProgress";
import { useNavigate } from "react-router-dom";
import { CameraSection } from "./camerasectioninterview";
import Header from "../../pages/home/homeHeader/Header/Header";
import { TranscriptSection } from "./transcriptsectioninterview";
import { useCamera } from "../../hooks/usecamerahook";
import { useInterview } from "../../hooks/useinterviewhook";
import { useMediaPipeViolations } from "../../hooks/usemediapipeviolations";
import { Violations } from "../violations/violations";
import screeningstatusupdate from "../../apis/candidatescreening/screeningstatusupdate";
import "../../pages/interview/interview.css";
import ConfirmExitDialog from "../enditerview/endinterview";

export default function AIInterview({ locationState }) {
  const navigate = useNavigate();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const {
    videoRef,
    cameraReady,
    micReady,
    previewStreamRef,
  } = useCamera();

  const {
    isRunning,
    isConnecting,
    status,
    transcripts,
    startInterview,
    stopInterview,
    isInterviewCompleted,
    isFinalizingInterview,
    timer,
    isMicrophoneEnabled,
    userId,
    jdId,
    isAliSpeaking,
  } = useInterview(previewStreamRef);
  const violations = useMediaPipeViolations(videoRef, isRunning);

  const sendExitEvent = async () => {
    try {
      let payload = {
        csId: userId,
        statuscode: 'interrupted'
      }
      await screeningstatusupdate(payload);
      await stopInterview({ shouldAnalyze: false });
    } catch (error) {
      console.error('Failed to update screening status:', error);
    }
  };

  const handleConfirmExit = async () => {
    setIsDialogOpen(false);
    const response = await sendExitEvent();
    if (response) {
      stopInterview();
      navigate("/end-interview", { state: { userId } });
    }
  };

  // Redirect to thank you page when interview is completed
  useEffect(() => {
    if (isInterviewCompleted) {
      navigate("/end-interview", { state: { userId } });
    }
  }, [isInterviewCompleted, navigate, userId]);

  useEffect(() => {
    const handleBeforeUnload = async (event) => {
      event.preventDefault();
      event.returnValue = ""; // Required for Chrome
      await sendExitEvent();
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  useEffect(() => {
      const onBack = () => {
        if (window.confirm("Are you sure you want to exit?")) {
          sendExitEvent();
          navigate("/end-interview", { state: { userId } });
        } else {
          window.history.pushState(null, "", window.location.pathname);
        }
      };
      window.history.pushState(null, "", window.location.pathname);
      window.addEventListener("popstate", onBack);
      return () => window.removeEventListener("popstate", onBack);   
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  return (
    <Box className="interview-container">
      <Header isInterviewMode={true} />

      <Box className="interview-container-box">
        {/* Left Side: User Camera (Full Height) */}
        <Box className="userCamera_interview">
          <CameraSection
            videoRef={videoRef}
            cameraReady={cameraReady}
            micReady={micReady}
            isRunning={isRunning}
            isConnecting={isConnecting}
            isInterviewCompleted={isInterviewCompleted}
            timer={timer}
            status={status}
            isMicrophoneEnabled={isMicrophoneEnabled}
            startInterview={startInterview}
            isAliSpeaking={isAliSpeaking}
          />
        </Box>

        {/* Right Side: Live Transcript */}
        <Box className="transcript_sidebar_interview">
          <TranscriptSection
            transcripts={transcripts}
            status={status}
            cameraReady={cameraReady}
            micReady={micReady}
          />
          <div className="end-interview-container">
            <button
              className="end-interview-btn"
              onClick={() => setIsDialogOpen(true)}
              disabled={
                isFinalizingInterview || 
                !locationState?.assessmentDurationInSeconds || 
                (locationState.assessmentDurationInSeconds - timer <= 5)
              }
            >
              End Interview
            </button>
          </div>
          
          <ConfirmExitDialog
            isOpen={isDialogOpen}
            onConfirm={handleConfirmExit}
            onCancel={() => setIsDialogOpen(false)}
          />

          <Violations userId={userId} jdId={jdId} violations={violations} timer={timer} isInterviewCompleted={isInterviewCompleted} />
        </Box>
      </Box>

      {isFinalizingInterview && (
        <div className="interview-processing-overlay">
          <div className="interview-processing-content">
            <CircularProgress size={42} />
            <p className="interview-processing-title">Analyzing interview</p>
            <p className="interview-processing-subtitle">Please wait while we finalize it all.</p>
          </div>
        </div>
      )}
    </Box>
  );
}